import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Search, Layers, Home, LogOut, ChevronDown, Shield, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import TrialBanner from "@/components/TrialBanner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navLinks = [
  { to: "/studio", label: "Studio", icon: Home, testid: "nav-studio" },
  { to: "/search", label: "Search", icon: Search, testid: "nav-search" },
  { to: "/compare", label: "Compare", icon: Layers, testid: "nav-compare" },
];

export default function Layout() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();

  const initials = (user?.name || user?.email || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();

  async function onLogout() {
    await logout();
    nav("/", { replace: true });
  }

  return (
    <div className="min-h-screen bg-white">
      <TrialBanner />
      <header
        className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-stone-100"
        data-testid="app-header"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link
            to="/studio"
            className="font-display text-xl tracking-tight text-stone-900 flex items-center gap-2"
            data-testid="brand-link"
          >
            <span className="w-2 h-2 rounded-full bg-[#C27A62]" />
            Atelier Studio
          </Link>
          <nav className="flex items-center gap-1">
            {navLinks.map(({ to, label, icon: Icon, testid }) => {
              const active =
                to === "/studio"
                  ? loc.pathname === "/studio"
                  : loc.pathname.startsWith(to);
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all",
                    active
                      ? "bg-stone-900 text-white"
                      : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                  )}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  <span className="hidden sm:inline">{label}</span>
                </Link>
              );
            })}
            {user && typeof user === "object" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-trigger"
                    className="ml-2 flex items-center gap-2 px-2 py-1 rounded-full hover:bg-stone-100 transition-colors"
                  >
                    <span
                      className="w-8 h-8 rounded-full bg-stone-900 text-white text-xs font-semibold flex items-center justify-center"
                      data-testid="user-avatar"
                    >
                      {initials}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-stone-500" strokeWidth={1.5} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[220px]">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col">
                      <span
                        className="text-sm text-stone-900 font-medium"
                        data-testid="user-menu-name"
                      >
                        {user.name || user.email}
                      </span>
                      <span
                        className="text-xs text-stone-500"
                        data-testid="user-menu-email"
                      >
                        {user.email}
                      </span>
                      {user.role === "admin" && (
                        <span
                          className="mt-1 text-[10px] uppercase tracking-[0.15em] text-[#C27A62] font-semibold"
                          data-testid="user-menu-admin-badge"
                        >
                          Admin
                        </span>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => nav("/profile")}
                    data-testid="profile-link"
                  >
                    <User className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Profile &amp; billing
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem
                      onClick={() => nav("/admin")}
                      data-testid="admin-panel-link"
                    >
                      <Shield className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Admin panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={onLogout}
                    data-testid="logout-button"
                    className="text-red-600 focus:text-red-700 focus:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>
        </div>
      </header>
      <main>
        <Outlet />
      </main>
    </div>
  );
}
